'use client'

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { useCurrentUser } from '../hooks/useCurrentUser'
import type { UserProfile } from '../lib/user.types'

type UserContextValue = {
  user: UserProfile | null
  // A null user is either still loading or has no profile; this tells them apart.
  isUserLoading: boolean
  projectId: string | null
  refetchProjectId: () => Promise<string | null>
}

const UserContext = createContext<UserContextValue | null>(null)

export function UserProvider({ children }: PropsWithChildren) {
  const { user, isLoading: isUserLoading, refetchUser } = useCurrentUser()

  const refetchProjectId = useCallback(async (): Promise<string | null> => {
    const freshUser = await refetchUser()
    return freshUser?.activeProjectId ?? null
  }, [refetchUser])

  const value = useMemo<UserContextValue>(
    () => ({
      user,
      isUserLoading,
      projectId: user?.activeProjectId ?? null,
      refetchProjectId,
    }),
    [user, isUserLoading, refetchProjectId],
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export function useUserProvider(): UserContextValue {
  const context = useContext(UserContext)

  if (!context) {
    throw new Error('useUserProvider must be used within a UserProvider')
  }

  return context
}
